import { callApi } from "@src/utils/api-client";
import { getFailureTypeForHttpStatus } from "@src/utils/failure-taxonomy";
import ClientLogsService from "./client-logs.service";
import { buildPartnersConfig } from "./configs/partners.config";

export interface Partner {
  id: string;
  name: string;
  filtered_insurance_packages?: Record<string, number[]>;
  branding?: string;
}

interface PartnerBackendResponse {
  partnerId: string;
  partnerName: string;
  partnerBranding?: string;
  filtered_insurance_packages?: Record<string, number[]>;
}

export interface PartnersError {
  error: string;
}

const partnersCache = new Map<string, Partner[]>();
const inflightPartnerRequests = new Map<string, Promise<Partner[] | PartnersError>>();

const getPartnersCacheKey = (parentOrganization: string): string => {
  return parentOrganization.trim();
};

export const clearPartnersCache = () => {
  partnersCache.clear();
  inflightPartnerRequests.clear();
};

export const fetchPartners = async (parent_organization: string = "", userUuid?: string) => {
  const normalizedParentOrganization = parent_organization.trim();
  const cacheKey = getPartnersCacheKey(normalizedParentOrganization);

  const cachedPartners = partnersCache.get(cacheKey);
  if (cachedPartners) {
    return cachedPartners;
  }

  const inflightRequest = inflightPartnerRequests.get(cacheKey);
  if (inflightRequest !== undefined) {
    return inflightRequest;
  }

  const requestPromise = (async (): Promise<Partner[] | PartnersError> => {
    try {
      const { data, response } = await callApi(
        buildPartnersConfig(normalizedParentOrganization, userUuid),
      );

      if (!response.ok) {
        const errorMessage = `API responded with status ${response.status}: ${response.statusText}`;
        ClientLogsService.logError(
          "system",
          "partners.service.fetchPartners",
          "Failed to fetch partners data",
          new Error(errorMessage),
          {
            errorType: getFailureTypeForHttpStatus(response.status),
            dependencyName: "PartnersAPI",
            functionName: "fetchPartners",
          },
        );

        return { error: errorMessage };
      }

      const partnerResponses = (data as { partners?: PartnerBackendResponse[] })?.partners;
      const partners = Array.isArray(partnerResponses)
        ? partnerResponses.map((partner) => ({
            id: partner.partnerId,
            name: partner.partnerName,
            ...(partner.partnerBranding !== undefined ? { branding: partner.partnerBranding } : {}),
            ...(partner.filtered_insurance_packages !== undefined
              ? {
                  filtered_insurance_packages: partner.filtered_insurance_packages,
                }
              : {}),
          }))
        : [];
      partnersCache.set(cacheKey, partners);

      return partners;
    } catch (error: unknown) {
      ClientLogsService.logError(
        "system",
        "partners.service.fetchPartners",
        "Error fetching partners data",
        error instanceof Error ? error : new Error("Unknown error"),
        {
          errorType: "DependencyFailure",
          dependencyName: "PartnersAPI",
          functionName: "fetchPartners",
        },
      );

      const errorMessage =
        error instanceof Error ? error.message || "Unknown error" : "Unknown error";

      return {
        error: errorMessage,
      };
    } finally {
      inflightPartnerRequests.delete(cacheKey);
    }
  })();

  inflightPartnerRequests.set(cacheKey, requestPromise);

  return requestPromise;
};
